import React, { useState, useEffect } from 'react';

import application from 'application';

import eventLocationTypes from 'constants/event-location-types';

import { useForm } from 'generics/form';
import SingleSelect from 'generics/components/single-select';
import DisplayTextToCopy from 'generics/components/display-text-to-copy';

import {
  getMinimumStartTime,
  formatDateObjectToBrowserDate
} from 'utilities/date';

import * as helpers from './helpers';
import * as eventHandlers from './event-handlers';
import fieldIndices from './field-indices';

import LocationTypeInput from './location-type-input';

import './index.scss';

function GenericEventEditor(props) {

  const postId = props.postId;
  const recurField = props.recurField;
  const error = props.error;
  const getEvent = props.getEvent;
  const updateEvent = props.updateEvent;
  const _validator = props.validator;
  const deleteEditorNode = props.deleteEditorNode;
  const resetError = props.resetError;

  const defaultValues = helpers.getDefaultValues();

  const [
    fieldValues,
    setValueByIndex, errorMessages, setErrorMessages,
    addFields, modifiedStates, setModifiedStateByIndex
  ] = useForm(defaultValues, onValuesChange, validator, helpers.exceptionToErrorMessages);

  const [rootState, setRootState] = useState({
    event: null,
    loading: true
  });

  useEffect(function onLoad() {

    loadEvent();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onErrorChange() {

    tryHandleErrorChange();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  function onValuesChange(values) {
    updateEventEditorValues(values);
  }

  async function tryHandleErrorChange() {

    if (error !== true) {
      return;
    }

    resetError();

    const forceDisplayErrors = true;

    validate(fieldValues, forceDisplayErrors);

  }

  async function updateEventEditorValues(values) {

    try {

      await updateEvent(postId, helpers.valuesToJSON(rootState, values));

    } catch (exception) {

      const errorMessages = helpers.exceptionToErrorMessages(exception);
      const forceDisplayErrors = false;

      helpers.trySetErrorMessages(errorMessages, forceDisplayErrors, setErrorMessages, setRootState);

    }
  }

  async function validator(values) {

    return _validator(postId, helpers.valuesToJSON(rootState, values));

  }

  async function validate(values, forceDisplayErrors) {

    try {

      await validator(values);

    } catch (exception) {

      const errorMessages = helpers.exceptionToErrorMessages(exception);

      helpers.trySetErrorMessages(errorMessages, forceDisplayErrors, setErrorMessages, setRootState);

    }

  }

  async function loadEvent() {

    setRootState(function updateRootState(_rootState) {
      return {
        ..._rootState,
        loading: true
      };
    });

    const event = await getEvent(postId);

    const mode = helpers.getEditorMode(event);

    helpers.setEventFieldsInForm(event, setValueByIndex);

    setRootState(function updateRootState(_rootState) {
      return {
        ..._rootState,
        event,
        mode,
        loading: false
      };
    });

  }

  function renderErrorMessage(fieldIndex) {

    const errorMessage = errorMessages[fieldIndex];

    if (errorMessage === '') {
      return;
    }

    return <label className="error-message">{errorMessage}</label>;

  }

  function tryRenderTimezoneClarificationText(displayTimezoneClarificationText = false) {

    if (displayTimezoneClarificationText === false) {
      return;
    }

    return <label className="timezone-clarification-message">Time is always displayed in your local timezone</label>;

  }

  function renderDateInput(fieldIndex, label, dateInputAttributes, displayTimezoneClarificationText) {

    return <div className="event-date-time-input-segment">

      <label className="field-label">{label}</label>

      <div className="event-date-tine-input-container">

        <input {...dateInputAttributes} />

        {tryRenderTimezoneClarificationText(displayTimezoneClarificationText)}

        {renderErrorMessage(fieldIndex)}

      </div>

    </div>;

  }

  function renderStartDateTimeInputs() {

    const startDateLabel = 'Start date';
    const startTimeLabel = 'Start time';
    const date = new Date();

    const minimumDate = formatDateObjectToBrowserDate(date);
    const minimumTime = getMinimumStartTime(date);

    const startDatePickerControlAttributes = {
      type: 'date',
      className: 'event-date-input',
      value: fieldValues[fieldIndices.START_DATE_INDEX],
      min: minimumDate,
      disabled: helpers.isEditorDisabled(rootState),
      onChange(event) {
        eventHandlers.handleStartDateControlChange(event, setValueByIndex, setModifiedStateByIndex);
      },
      onKeyDown(event) {
        event.preventDefault();
      },
      onClick(event) {
        event.target.showPicker();
      }
    };

    const startTimePickerControlAttributes = {
      type: 'time',
      className: 'event-time-input',
      value: fieldValues[fieldIndices.START_TIME_INDEX],
      min: minimumTime,
      disabled: helpers.isEditorDisabled(rootState),
      onChange(event) {
        eventHandlers.handleStartTimeControlChange(event, setValueByIndex, setModifiedStateByIndex);
      },
      onKeyDown(event) {
        event.preventDefault();
      },
      onClick(event) {
        event.target.showPicker();
      }
    };

    return <div className="event-start-date-time-section">
      {renderDateInput(fieldIndices.START_DATE_INDEX, startDateLabel, startDatePickerControlAttributes)}
      {renderDateInput(fieldIndices.START_TIME_INDEX, startTimeLabel, startTimePickerControlAttributes, true)}
    </div>;

  }

  function renderEndDateTimeInputs() {

    const endDateLabel = 'End date';
    const endTimeLabel = 'End time';
    const date = new Date();

    const minimumDate = formatDateObjectToBrowserDate(date);
    const minimumTime = getMinimumStartTime(date);

    const endDatePickerControlAttributes = {
      type: 'date',
      className: 'event-date-input',
      value: fieldValues[fieldIndices.END_DATE_INDEX],
      min: minimumDate,
      disabled: helpers.isEditorDisabled(rootState),
      onChange(event) {
        eventHandlers.handleEndDateControlChange(event, setValueByIndex, setModifiedStateByIndex);
      },
      onKeyDown(event) {
        event.preventDefault();
      },
      onClick(event) {
        event.target.showPicker();
      }
    };

    const endTimePickerControlAttributes = {
      type: 'time',
      className: 'event-time-input',
      value: fieldValues[fieldIndices.END_TIME_INDEX],
      min: minimumTime,
      disabled: helpers.isEditorDisabled(rootState),
      onChange(event) {
        eventHandlers.handleEndTimeControlChange(event, setValueByIndex, setModifiedStateByIndex);
      },
      onKeyDown(event) {
        event.preventDefault();
      },
      onClick(event) {
        event.target.showPicker();
      }
    };

    return <div className="event-end-date-time-section">
      {renderDateInput(fieldIndices.END_DATE_INDEX, endDateLabel, endDatePickerControlAttributes)}
      {renderDateInput(fieldIndices.END_TIME_INDEX, endTimeLabel, endTimePickerControlAttributes, true)}
    </div>;

  }

  function renderDurationSection() {

    return <div className="generic-event-editor-section duration-section">

      <div className="event-start-date-time-section-container">
        {renderStartDateTimeInputs()}
      </div>

      <div className="event-end-date-time-section-container">
        {renderEndDateTimeInputs()}
      </div>

    </div>;

  }

  function renderRecurTypeSection() {

    const recurTypeSelectProperties = {
      options: helpers.getRecurTypeOptions(fieldValues),
      value: fieldValues[fieldIndices.RECUR_TYPE_INDEX],
      disabled: helpers.isEditorDisabled(rootState),
      onChange(option) {
        eventHandlers.handleEventRecurringControlChange(option, setValueByIndex);
      },
      labelFieldName: 'label',
      valueFieldName: 'value'
    };

    return <div className="generic-event-editor-section">

      <label className="field-label">Recurring</label>

      <SingleSelect {...recurTypeSelectProperties} />

    </div>;

  }

  function tryRenderRecurTypeSection() {

    if (recurField !== true) {
      return;
    }

    return renderRecurTypeSection();

  }

  function renderLocationTypeInput(label, fieldValue) {

    const fieldIndex = fieldIndices.LOCATION_TYPE_INDEX;

    const properties = {
      label,
      fieldValue,
      activeValue: fieldValues[fieldIndex],
      onSelect(value) {
        eventHandlers.handleEventLocationControlChange(value, setValueByIndex);
      }
    };

    return <LocationTypeInput {...properties} />;

  }

  function tryRenderLocationValueInput() {

    const fieldIndex = fieldIndices.LOCATION_TYPE_INDEX;

    if (fieldValues[fieldIndex] === eventLocationTypes.HELPTAP_LIVESTREAM) {
      return;
    }

    const locationValueFieldIndex = fieldIndices.LOCATION_VALUE_INDEX;

    const otherLocationValueInputAttributes = {
      className: 'location-addess-control',
      type: 'text',
      placeholder: 'Give joining instructions',
      value: fieldValues[locationValueFieldIndex],
      disabled: helpers.isEditorDisabled(rootState),
      onPaste(event) {
        eventHandlers.handleEventLocationAddressControlChange(event, setValueByIndex);
        event.stopPropagation();
      },
      onChange(event) {
        eventHandlers.handleEventLocationAddressControlChange(event, setValueByIndex);
      }
    };

    return <div className="event-other-location-content-container">

      <input {...otherLocationValueInputAttributes} />

      {renderErrorMessage(locationValueFieldIndex)}

    </div>;

  }

  function tryRenderLiveStreamLink() {

    const fieldIndex = fieldIndices.LOCATION_TYPE_INDEX;

    if (fieldValues[fieldIndex] === eventLocationTypes.OTHERS) {
      return;
    }

    const startup = application.getCurrentStartup();
    const startupAddress = startup.address.url;
    const text = `${startupAddress}/community/${postId}/live`;

    const properties = {
      text
    };

    return <DisplayTextToCopy {...properties} />;

  }

  function renderLocationSection() {

    return <div className="generic-event-editor-section location-section">

      <div className="generic-event-editor-section-label-container">
        <label className="field-label">Location</label>
        <label className="field-label sub-text">Choose a way for people to join your event</label>
      </div>

      <div className="event-location-type-inputs-container">

        {tryRenderLocationValueInput()}

        {tryRenderLiveStreamLink()}

      </div>

    </div>;

  }

  function renderNotifyBeforeSection() {

    const notifyBeforeSelectProperties = {
      options: helpers.getNotifyBeforeOptions(),
      value: fieldValues[fieldIndices.NOTIFY_BEFORE_INDEX],
      disabled: helpers.isEditorDisabled(rootState),
      onChange(option) {
        eventHandlers.handleEventNotificationControlChange(option, setValueByIndex);
      },
      labelFieldName: 'label',
      valueFieldName: 'value'
    };

    return <div className="generic-event-editor-section">

      <label className="field-label">Notify</label>

      <SingleSelect {...notifyBeforeSelectProperties} />

      {renderErrorMessage(fieldIndices.NOTIFY_BEFORE_INDEX)}

    </div>;

  }

  function renderRemoveButtonSection() {

    const removeEventButtonAttributes = {
      type: 'button',
      className: 'delete-button application-delete-button',
      onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        deleteEditorNode();
        return false;
      }
    };

    return <div className="generic-event-editor-section remove-button-section">

      <button {...removeEventButtonAttributes}>Remove</button>

    </div>;

  }

  function renderForm() {

    return <form className="generic-event-editor-form">

      {renderDurationSection()}

      {tryRenderRecurTypeSection()}

      {renderLocationSection()}

      {renderNotifyBeforeSection()}

      {renderRemoveButtonSection()}

    </form>;

  }

  function tryRenderForm() {

    if (rootState.event === null) {
      return;
    }

    return renderForm();

  }

  function tryRenderSpinner() {

    if (rootState.event !== null) {
      return;
    }

    return <div className="spinner-container">
      <ht-spinner size="small" />
    </div>;

  }

  function renderMain() {

    const mainAttributes = {
      contentEditable: false,
      className: 'event-editor-element generic-event-editor-main'
    };

    return <div {...mainAttributes}>
      {tryRenderSpinner()}
      {tryRenderForm()}
    </div>;

  }

  return renderMain();

}

export default GenericEventEditor;
